import { Divider } from 'antd';
import classnames from 'classnames';
import { UncontrolledTooltip } from 'reactstrap';
import { zeroPad } from '@/shared/lib';
import { AutoCaptureStripIcon as autoCaptureIc } from '@/shared/ui';

import './styles.scss';
import ThumbnailTooltip from './thumbnailTooltip';
import useThumbnailEvent from './useThumbnailEvent';

export type ThumbnailEventProps = {
  id: number | string;
  disabled?: boolean;
  sizeThumbnailConfig: any;
  onClickSelect?: (e: any) => void;
  isSelected?: boolean;
  isActive?: boolean;
  className?: string;
  showTypeEvent?: boolean;
  event?: any;
  // never read inside this component, but forwarded from DeviceEventPreviewStrip/
  // eventPreviewStrip and asserted by deviceEventPreviewStrip.test.tsx - keep declared.

  isChanged?: boolean;
};

/**
 * Renders one strip-management event thumbnail with its time header, badges, strip image and comment footer. Reads the image URL and load-error state from useThumbnailEvent, unpacks the event display flags, shows the include-strip, include-report, validity and auto-capture badges, and replaces the image with a no-data or load-error message when no strip URL resolves.
 *
 * @param props - The event record, thumbnail size config, selection state and click handler.
 * @returns The event thumbnail button with its header badges, strip image and comment row.
 */
const ThumbnailEvent = ({
  id,
  disabled = false,
  sizeThumbnailConfig,
  onClickSelect,
  isSelected,
  isActive,
  className,
  showTypeEvent,
  event,
}: ThumbnailEventProps) => {
  const { isError, setIsError, stripUrl, stripStartTime } = useThumbnailEvent({ event });

  const {
    eventTime,
    isIncludedStrip,
    isIncludedToReport,
    isShowValid,
    isRemoved,
    isValid,
    comment,
    updateStatus,
    typeText,
    symptoms,
    isOtherEvent,
    title,
    isShowSymptoms,
    isDeviceEvent,
    friendlyId,
    isAutoCapture,
    isNoEcgData,
  } = event || {};
  const { width, height, imageWidth, imageHeight, textWidth, commentWidth } =
    sizeThumbnailConfig || {};

  /**
   * Renders the trailing label in a strip thumbnail's top-left corner, showing either the symptoms or the event type. Sums the widths already taken by the device tag and the include, validity and auto-capture badges, subtracts that from the available text width, and yields nothing when neither symptoms nor a type label apply.
   *
   * @returns A divider plus the symptoms or type tooltip, or null when neither applies.
   */
  const renderTopLeftContent = () => {
    const existWidth =
      (isDeviceEvent ? 50 : 2) +
      (isIncludedToReport && !isRemoved ? 86 : 0) +
      (isIncludedStrip && !isRemoved ? 64 : 0) +
      (isShowValid ? 48 : 0) +
      (isAutoCapture ? 20 : 0);
    if (isShowSymptoms) {
      return (
        <>
          <Divider className="top-content-left__divider" type="vertical" aria-hidden="true" />
          <ThumbnailTooltip title="Symptoms:" content={symptoms} width={textWidth - existWidth} />
        </>
      );
    }
    if (showTypeEvent || isDeviceEvent) {
      const text = `${isOtherEvent ? title : typeText}`;
      return (
        <>
          <Divider className="top-content-left__divider" type="vertical" aria-hidden="true" />
          <ThumbnailTooltip title="Type:" content={text} width={textWidth - existWidth} />
        </>
      );
    }
    return null;
  };

  return (
    <button
      id={id as any} // pre-existing: id can be a number, but button id expects string
      disabled={disabled}
      className={classnames(
        'single-strip-management-event',
        className,
        updateStatus ? '--changed' : '',
        isSelected ? '--selected' : '',
        isActive ? '--active' : '',
      )}
      onClick={onClickSelect}
      style={{ width, height }}
    >
      <div className="single-strip-management-event__top-content">
        <div className="top-content-left">
          {isDeviceEvent && (
            <div className="device-event-tag">{friendlyId ? `#${zeroPad(friendlyId)}` : ''}</div>
          )}
          {isDeviceEvent && (
            <Divider className="top-content-left__divider" type="vertical" aria-hidden="true" />
          )}
          <div className="event-time">{isDeviceEvent ? eventTime : stripStartTime}</div>
          {renderTopLeftContent()}
        </div>
        <div className="top-content-right">
          {isIncludedStrip && !isRemoved && (
            <div className="include-tag include-strip">Incl. strip</div>
          )}
          {isIncludedToReport && !isRemoved && (
            <div className="include-tag include-report">Incl. in report </div>
          )}
          {isShowValid && (
            <div className={classnames('valid-tag', isValid ? 'is-valid' : '')}>
              {isValid ? 'Valid' : 'Invalid'}
            </div>
          )}
          <div id={`auto-capture-ic-${id}`}>
            {isAutoCapture && (
              <div className={classnames('auto-capture-tag')}>
                <img src={autoCaptureIc} alt="" />
                <UncontrolledTooltip
                  className="custom-tooltip"
                  target={`auto-capture-ic-${id}`}
                  placement="bottom"
                  delay={{ show: 0, hide: 0 }}
                  hideArrow
                >
                  This event is automatically captured
                </UncontrolledTooltip>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="thumbnail-image-wrapper">
        {stripUrl ? (
          isError ? (
            <div className="no-data-display" style={{ width: imageWidth, height: imageHeight }}>
              There is an error while loading strips
            </div>
          ) : (
            <img
              onError={() => {
                setIsError(true);
              }}
              style={{ width: imageWidth, height: imageHeight }}
              className="single-strip-management-event__image selectDisable"
              src={stripUrl}
              alt=""
            />
          )
        ) : (
          <div className="no-data-display" style={{ width: imageWidth, height: imageHeight }}>
            {isNoEcgData
              ? 'This strip image is currently unavailable because patient captured event in no data zone'
              : 'The strip image is currently unavailable'}
          </div>
        )}
        <div className="layer-prevent-select-image" />
      </div>
      <div className="single-strip-management-event__bottom-content">
        <ThumbnailTooltip
          content={comment}
          title="Comment:"
          width={commentWidth - (updateStatus ? 58 : 0)}
        />
        {updateStatus && <div className="bottom-content-right">{updateStatus}</div>}
      </div>
    </button>
  );
};

export default ThumbnailEvent;
